import { getNestedValue } from '@/lib/image-generation-props';
import { isBlockBackedSection, isPageTitleSectionType } from '@/lib/section-registry';
import type { PageBlock } from '@/types/block';
import { isPendingAIImage } from '@/types/image';
import type { Page } from '@/types/page';
import type { PageSection } from '@/types/page-section';

export interface AIImageGenerationConfig {
  sectionImage?: { field: string };
  arrayItemImages?: { arrayField: string; imageField: string };
}

export interface SectionMatch {
  section: PageSection;
  itemId: string | null;
  aiConfig: AIImageGenerationConfig;
}

export interface BlockImageMatch {
  section: PageSection;
  blockId: string;
  imageField: 'image' | 'backgroundImage';
}

export interface CollectionItemImageMatch {
  section: PageSection;
  blockId: string;
  itemId: string;
  slotKey: string;
}

function propsRecord(props: unknown): Record<string, unknown> {
  return props && typeof props === 'object' && !Array.isArray(props)
    ? (props as Record<string, unknown>)
    : {};
}

export function getPendingRunId(value: unknown): string | null {
  if (!isPendingAIImage(value)) return null;
  return value.runId ?? null;
}

export function getFreeformSectionAiConfig(section: PageSection): AIImageGenerationConfig | null {
  if (
    !isBlockBackedSection(section.sectionTemplateCode, section.sectionType) &&
    !isPageTitleSectionType(section.sectionType)
  ) {
    return null;
  }

  return { sectionImage: { field: 'backgroundImage' } };
}

/**
 * Searches all sections in the page to find which section/item contains the given runId.
 * Returns the section and optional itemId.
 */
export function findSectionByRunId(
  page: Page | null | undefined,
  runId: string,
): SectionMatch | null {
  if (!page?.slots?.main?.sections) return null;

  for (const section of page.slots.main.sections) {
    const aiConfig = getFreeformSectionAiConfig(section);

    if (!aiConfig) continue;

    // Check section-level image
    if (aiConfig.sectionImage) {
      const imageValue = getNestedValue(section.props, aiConfig.sectionImage.field);

      const pendingRunId = getPendingRunId(imageValue);
      if (pendingRunId && pendingRunId === runId) {
        return { section, itemId: null, aiConfig };
      }
    }

    // Check array items
    if (aiConfig.arrayItemImages) {
      const { arrayField, imageField } = aiConfig.arrayItemImages;
      const items = section.props[arrayField] || [];

      if (Array.isArray(items)) {
        for (const item of items) {
          const imageValue = getNestedValue(item, imageField);

          const pendingRunId = getPendingRunId(imageValue);
          if (pendingRunId && pendingRunId === runId && typeof item.id === 'string') {
            return { section, itemId: item.id, aiConfig };
          }
        }
      }
    }
  }

  return null;
}

export function collectPendingImageRunIds(
  props: Record<string, unknown>,
  aiConfig: AIImageGenerationConfig,
): string[] {
  const runIds: string[] = [];

  if (aiConfig.sectionImage) {
    const imageValue = getNestedValue(props, aiConfig.sectionImage.field);
    const pendingRunId = getPendingRunId(imageValue);
    if (pendingRunId) {
      runIds.push(pendingRunId);
    }
  }

  if (aiConfig.arrayItemImages) {
    const { arrayField, imageField } = aiConfig.arrayItemImages;
    const rawItems = props[arrayField];

    if (Array.isArray(rawItems)) {
      for (const item of rawItems) {
        if (!item || typeof item !== 'object') continue;
        const record = item as Record<string, unknown>;
        const imageValue = getNestedValue(record, imageField);

        const pendingRunId = getPendingRunId(imageValue);
        if (pendingRunId && typeof record.id === 'string') {
          runIds.push(pendingRunId);
        }
      }
    }
  }

  return runIds;
}

export function collectPendingBlockImageRunIds(blocks: PageBlock[] | undefined): string[] {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return [];
  }

  const runIds: string[] = [];
  for (const block of blocks) {
    const props = propsRecord(block.props);

    if (block.blockType === 'image') {
      const imageRunId = getPendingRunId(props.image);
      if (imageRunId) {
        runIds.push(imageRunId);
      }
    } else if (block.blockType === 'group') {
      const bgRunId = getPendingRunId(props.backgroundImage);
      if (bgRunId) {
        runIds.push(bgRunId);
      }
    } else if (block.blockType === 'collection') {
      const items = Array.isArray(props.items) ? props.items : [];
      for (const item of items) {
        if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
        for (const val of Object.values(item as Record<string, unknown>)) {
          const runId = getPendingRunId(val);
          if (runId) runIds.push(runId);
        }
      }
    }
  }

  return runIds;
}

export function findCollectionItemImageByRunId(
  page: Page | null | undefined,
  runId: string,
): CollectionItemImageMatch | null {
  if (!page?.slots?.main?.sections) return null;

  for (const section of page.slots.main.sections) {
    const blocks = Array.isArray(section.blocks) ? section.blocks : [];
    for (const block of blocks) {
      if (block.blockType !== 'collection') continue;
      const props = propsRecord(block.props);
      const items = Array.isArray(props.items) ? props.items : [];
      for (const item of items) {
        if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
        const record = item as Record<string, unknown>;
        if (typeof record.id !== 'string') continue;
        for (const [key, val] of Object.entries(record)) {
          if (key === 'id') continue;
          const itemRunId = getPendingRunId(val);
          if (itemRunId && itemRunId === runId) {
            return { section, blockId: block.id, itemId: record.id, slotKey: key };
          }
        }
      }
    }
  }

  return null;
}

export function findBlockImageByRunId(
  page: Page | null | undefined,
  runId: string,
): BlockImageMatch | null {
  if (!page?.slots?.main?.sections) return null;

  for (const section of page.slots.main.sections) {
    const blocks = Array.isArray(section.blocks) ? section.blocks : [];
    for (const block of blocks) {
      const props = propsRecord(block.props);
      if (block.blockType === 'image') {
        const imageRunId = getPendingRunId(props.image);
        if (imageRunId && imageRunId === runId) {
          return { section, blockId: block.id, imageField: 'image' };
        }
        continue;
      }
      if (block.blockType === 'group') {
        const bgRunId = getPendingRunId(props.backgroundImage);
        if (bgRunId && bgRunId === runId) {
          return { section, blockId: block.id, imageField: 'backgroundImage' };
        }
      }
    }
  }

  return null;
}
